"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, Sparkles, CircleX as XCircle, CircleCheck as CheckCircle2, ArrowRight, Clock, Target, ShieldCheck, TrendingUp, PiggyBank } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const comparisonPoints = [{
  id: "access",
  title: "100% Direct Senior Access",
  iconName: "UserCheck",
  consultantValue: "You work directly with me — Yassine. Every audit, strategy session, and technical update is executed by a senior SEO expert.",
  agencyValue: "Pitched by senior directors, but your daily account is passed to junior executives or rotating interns.",
  pinkHighlight: "No middle managers. No lost communication."
}, {
  id: "cost",
  title: "Lean & Cost Efficient",
  iconName: "PiggyBank",
  consultantValue: "100% of your retainer goes directly into high-impact strategy, link analysis, and technical SEO deliverables.",
  agencyValue: "Up to 50% of your fee covers expensive city office overheads, sales commission, and account management layers.",
  pinkHighlight: "Maximum SEO ROI without agency markups."
}, {
  id: "strategy",
  title: "Bespoke SEO Roadmap",
  iconName: "Target",
  consultantValue: "Custom strategy tailored specifically to your market positioning, technical stack, and sustainable revenue targets.",
  agencyValue: "Standardized cookie-cutter checklists and copy-paste monthly reporting templates.",
  pinkHighlight: "Tailored directly to your revenue goals."
}, {
  id: "agility",
  title: "Agility & Rapid Execution",
  iconName: "Clock",
  consultantValue: "Direct line to me via Slack or email. Fast pivot capability when search algorithms change or priorities shift.",
  agencyValue: "Multi-layered sign-offs, ticketing queues, and rigid scope limits that stall critical site fixes.",
  pinkHighlight: "Immediate responses & swift implementation."
}, {
  id: "accountability",
  title: "Honest Measurement & Ownership",
  iconName: "ShieldCheck",
  consultantValue: "I measure performance on genuine business impact — organic revenue, qualified leads, and rankings that stick.",
  agencyValue: "Focus on superficial vanity metrics like impression spikes or irrelevant keyword rankings to hide slow progress.",
  pinkHighlight: "Transparent metrics tied to business growth."
}, {
  id: "capacity",
  title: "Dedicated Focus & Low Client Ratio",
  iconName: "TrendingUp",
  consultantValue: "I limit my client intake so I can deeply understand your business model and deliver exceptional technical depth.",
  agencyValue: "Account managers handle 15 to 20 clients simultaneously, leading to rushed work and reactive communication.",
  pinkHighlight: "Uncompromising quality and deep focus."
}];
export default function ConsultantBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-accent/10 blur-[120px] rounded-full pointer-events-none -z-10" />
      <div className="absolute bottom-10 right-10 w-[300px] h-[300px] bg-primary/5 blur-[100px] rounded-full pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-foreground border border-accent/30 rounded-full">
            <Sparkles className="size-3.5 text-accent mr-1.5 inline" />
            Direct Expertise Vs Agency Overhead
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Why Work Directly With Me Instead of an Agency?
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            When you hire YE Consultancy, you don&apos;t get delegated to a junior team. You work directly with me — Yassine Essaadi. Here is how my personal hands-on approach delivers superior results and true cost efficiency.
          </p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {comparisonPoints.map((point, i) => <motion.div key={point.id} data-index={i} variants={itemVariants} className="h-full">
              <Card className="h-full bg-card border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-accent/40 flex flex-col justify-between overflow-hidden relative group">
                {}
                <div className="h-1 w-full bg-gradient-to-r from-primary via-accent to-primary opacity-80" />
                
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-primary font-semibold">
                      {i + 1}
                    </div>
                    <Badge variant="outline" className="text-xs bg-accent/10 border-accent/30 text-foreground font-medium">
                      {point.pinkHighlight}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-semibold font-default text-foreground pt-1">
                    {point.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4 flex-grow text-sm font-default">
                  {}
                  <div className="bg-background/80 rounded-lg p-3.5 border border-primary/15 space-y-1.5">
                    <div className="flex items-center text-primary font-medium text-xs tracking-wide uppercase">
                      <CheckCircle2 className="size-4 mr-1.5 text-primary shrink-0" />
                      With Me (Yassine)
                    </div>
                    <p className="text-foreground leading-snug">
                      {point.consultantValue}
                    </p>
                  </div>

                  {}
                  <div className="bg-muted/50 rounded-lg p-3.5 border border-border/60 space-y-1.5">
                    <div className="flex items-center text-muted-foreground font-medium text-xs tracking-wide uppercase">
                      <XCircle className="size-4 mr-1.5 text-muted-foreground shrink-0" />
                      Traditional Agency
                    </div>
                    <p className="text-muted-foreground leading-snug">
                      {point.agencyValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {}
        <div className="mt-16 bg-card border border-border rounded-2xl p-6 sm:p-8 lg:p-10 shadow-sm relative overflow-hidden">
          <div className="absolute -right-12 -bottom-12 size-64 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative z-10">
            <div className="lg:col-span-8 space-y-3">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-accent">
                <span className="size-2 rounded-full bg-accent" />
                Personal Commitment
              </div>
              <h3 className="text-2xl sm:text-3xl font-semibold font-default text-foreground">
                &ldquo;My reputation is built on transparent strategy and measurable search growth.&rdquo;
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed font-default">
                I take personal accountability for every strategy I recommend. When we work together, you get transparent reporting, realistic expectations, and hands-on execution focused on growing your UK or international search visibility.
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center items-stretch lg:items-end">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border hover:bg-secondary text-foreground">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="mt-12 pt-8 border-t border-border/60 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="space-y-1">
            <p className="text-xl sm:text-2xl font-bold font-default text-foreground">Direct Access</p>
            <p className="text-xs text-muted-foreground font-default">No account manager layer</p>
          </div>
          <div className="space-y-1">
            <p className="text-xl sm:text-2xl font-bold font-default text-foreground">100% Tailored</p>
            <p className="text-xs text-muted-foreground font-default">No generic templates</p>
          </div>
          <div className="space-y-1">
            <p className="text-xl sm:text-2xl font-bold font-default text-foreground">Transparent</p>
            <p className="text-xs text-muted-foreground font-default">Clear deliverables & fees</p>
          </div>
          <div className="space-y-1">
            <p className="text-xl sm:text-2xl font-bold font-default text-foreground">UK & Global</p>
            <p className="text-xs text-muted-foreground font-default">National & International SEO</p>
          </div>
        </div>
      </div>
    </section>;
}